import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Truck, RotateCcw, ShieldCheck, Lock, Headphones, BadgeCheck, Star, Cpu } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useI18n } from "@/lib/i18n";
import { api } from "@/lib/api";
import ProductCard from "@/components/store/ProductCard";

const HERO = "https://images.unsplash.com/photo-1788458740441-90052e49a75a?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";

export const Section = ({ eyebrow, title, link, children, testid }) => {
  const { t, dir } = useI18n();
  const Arrow = dir === "rtl" ? ArrowLeft : ArrowRight;
  return (
    <section data-testid={testid} className="container-x mt-20">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div className="space-y-2">{eyebrow && <p className="eyebrow">{eyebrow}</p>}<h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{title}</h2></div>
        {link && <Link to={link} className="text-sm font-semibold text-[#2563EB] flex items-center gap-1 shrink-0">{t("home.view_all")}<Arrow className="w-4 h-4" /></Link>}
      </div>
      {children}
    </section>
  );
};

const Grid = ({ items }) => <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">{items.map((p, i) => <ProductCard key={p.product_id} p={p} i={i} />)}</div>;

function Hero() {
  const { t, dir } = useI18n();
  const Arrow = dir === "rtl" ? ArrowLeft : ArrowRight;
  return (
    <section className="relative overflow-hidden bg-[#0B0D0F] text-white grain">
      <div className="absolute inset-0 grid-lines" />
      <div className="absolute -top-40 end-[-10%] w-[600px] h-[600px] rounded-full bg-[#2563EB]/30 blur-[120px]" />
      <div className="container-x relative grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center py-16 sm:py-24">
        <div className="space-y-7 rise">
          <span className="chip bg-white/10 text-white/90 border border-white/10"><Cpu className="w-3.5 h-3.5 text-[#60A5FA]" />{t("home.hero_badge")}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.15] tracking-tight">{t("home.hero_title")}<span className="block text-[#60A5FA]">{t("home.hero_title_accent")}</span></h1>
          <p className="text-base sm:text-lg text-white/70 max-w-xl leading-relaxed">{t("home.hero_sub")}</p>
          <div className="flex flex-wrap gap-3">
            <Link data-testid="hero-shop-btn" to="/products" className="btn-primary h-14 px-8 text-base">{t("home.hero_cta")}<Arrow className="w-5 h-5" /></Link>
            <Link data-testid="hero-track-btn" to="/track" className="btn h-14 px-8 bg-white/10 text-white border border-white/15 hover:bg-white/20">{t("nav.track")}</Link>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-3 pt-2 text-sm text-white/70">
            {[[ShieldCheck, "home.trust_secure"], [Truck, "home.trust_shipping"], [RotateCcw, "home.trust_returns"]].map(([I, k]) => <span key={k} className="flex items-center gap-2"><I className="w-4 h-4 text-[#60A5FA]" />{t(k)}</span>)}
          </div>
        </div>
        <div className="relative rise" style={{ animationDelay: "150ms" }}>
          <div className="aspect-[4/3] rounded-3xl overflow-hidden border border-white/10 shadow-2xl"><img src={HERO} alt="" className="w-full h-full object-cover" /></div>
          <div className="absolute -bottom-6 start-6 bg-white text-[#111827] rounded-2xl p-4 shadow-xl flex items-center gap-3 max-w-[260px]">
            <div className="w-10 h-10 rounded-xl bg-[#2563EB]/10 flex items-center justify-center"><Lock className="w-5 h-5 text-[#2563EB]" /></div>
            <div><p className="text-sm font-semibold">{t("home.secure_card_title")}</p><p className="text-xs muted">{t("home.secure_card_sub")}</p></div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { t, pick } = useI18n();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/home").then((r) => setD(r.data)).catch(() => setD({})); }, []);
  const benefits = [[BadgeCheck, "quality"], [Truck, "shipping"], [RotateCcw, "returns"], [Headphones, "support"]];
  const faqs = ["shipping_time", "payment", "returns", "warranty", "tracking"];
  const hasProducts = d && (d.latest || []).length > 0;
  return (
    <div data-testid="home-page">
      <Hero />
      {d && !hasProducts && (
        <div className="container-x mt-16"><div data-testid="home-empty" className="card p-10 text-center space-y-2"><p className="text-lg font-semibold">{t("home.coming_soon")}</p><p className="muted text-sm">{t("home.coming_soon_sub")}</p></div></div>
      )}
      {d?.categories?.length > 0 && (
        <Section title={t("home.categories")} testid="home-categories">
          <div className="flex gap-3 overflow-x-auto scrollbar-none pb-2">{d.categories.map((c) => <Link key={c.category_id} to={`/products?category=${c.category_id}`} className="btn-ghost shrink-0">{pick(c.name)}</Link>)}</div>
        </Section>
      )}
      {d?.featured?.length > 0 && <Section eyebrow={t("home.featured_eyebrow")} title={t("home.featured_title")} link="/products" testid="home-featured"><Grid items={d.featured} /></Section>}
      {d?.best_sellers?.length > 0 && <Section title={t("home.best_title")} link="/products?sort=best" testid="home-best"><Grid items={d.best_sellers} /></Section>}
      {d?.offers?.length > 0 && <Section eyebrow={t("home.offers_eyebrow")} title={t("home.offers_title")} link="/products?offer=1" testid="home-offers"><Grid items={d.offers} /></Section>}
      {hasProducts && !d.featured?.length && <Section title={t("home.latest_title")} link="/products" testid="home-latest"><Grid items={d.latest} /></Section>}

      <Section eyebrow={t("home.why_eyebrow")} title={t("home.why_title")} testid="home-benefits">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {benefits.map(([I, k], i) => (
            <div key={k} className="card p-6 space-y-3 rise" style={{ animationDelay: `${i * 70}ms` }}>
              <div className="w-11 h-11 rounded-xl bg-[#0B0D0F] flex items-center justify-center"><I className="w-5 h-5 text-[#60A5FA]" /></div>
              <h3 className="font-semibold">{t(`home.benefit_${k}`)}</h3><p className="text-sm muted leading-relaxed">{t(`home.benefit_${k}_sub`)}</p>
            </div>
          ))}
        </div>
      </Section>

      {d?.reviews?.length > 0 && (
        <Section title={t("home.reviews_title")} testid="home-reviews">
          <div className="grid md:grid-cols-3 gap-6">{d.reviews.map((r) => (
            <div key={r.review_id} className="card p-6 space-y-3">
              <div className="flex gap-0.5">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}</div>
              <p className="text-sm leading-relaxed">{r.comment}</p><p className="text-xs muted">{r.author} · {t("product.verified_purchase")}</p>
            </div>))}
          </div>
        </Section>
      )}

      <section className="container-x mt-20 grid lg:grid-cols-2 gap-6">
        <div data-testid="home-shipping-returns" className="rounded-3xl bg-[#0B0D0F] text-white p-8 sm:p-10 space-y-5 relative overflow-hidden">
          <div className="absolute inset-0 grid-lines opacity-60" />
          <div className="relative space-y-5">
            <h3 className="text-2xl font-bold">{t("home.ship_title")}</h3>
            {d?.shipping && <ul className="space-y-3 text-sm text-white/75">
              <li className="flex gap-3"><Truck className="w-5 h-5 text-[#60A5FA] shrink-0" />{t("home.ship_eta", { min: d.shipping.eta_days_min, max: d.shipping.eta_days_max })}</li>
              <li className="flex gap-3"><BadgeCheck className="w-5 h-5 text-[#60A5FA] shrink-0" />{t("home.ship_free", { amount: d.shipping.free_threshold / 100 })}</li>
              <li className="flex gap-3"><RotateCcw className="w-5 h-5 text-[#60A5FA] shrink-0" />{t("home.ship_returns", { days: d.returns.change_of_mind_days })}</li>
            </ul>}
            <Link to="/policy" className="btn bg-white text-[#0B0D0F] hover:bg-white/90">{t("footer.policy")}</Link>
          </div>
        </div>
        <div data-testid="home-payment-trust" className="card p-8 sm:p-10 space-y-5">
          <h3 className="text-2xl font-bold">{t("home.pay_title")}</h3>
          <p className="muted text-sm leading-relaxed">{t("home.pay_sub")}</p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {["pay_point_ssl", "pay_point_server", "pay_point_nocard", "pay_point_refund"].map((k) => <div key={k} className="flex items-center gap-2 bg-[#F5F7FA] rounded-xl p-3"><ShieldCheck className="w-4 h-4 text-[#16A34A] shrink-0" />{t(`home.${k}`)}</div>)}
          </div>
        </div>
      </section>

      <Section eyebrow={t("home.faq_eyebrow")} title={t("home.faq_title")} testid="home-faq">
        <Accordion type="single" collapsible className="card px-6">
          {faqs.map((f) => (
            <AccordionItem key={f} value={f}>
              <AccordionTrigger data-testid={`faq-${f}`} className="text-start text-base font-medium hover:no-underline">{t(`faq.${f}_q`)}</AccordionTrigger>
              <AccordionContent className="muted leading-relaxed">{t(`faq.${f}_a`)}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>
    </div>
  );
}
